import { useDrawer } from '@iveco-ti/iveco-ds'
import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { Megaphone, ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'
import { NewsHighlightedContentForm } from '../../schemas/NewsHighlightedContentSchema'
import { NewsHighlightedImageForm } from '../../schemas/NewsHighlightedImageSchema'
import { NewsHighlightedSummaryForm } from '../../schemas/NewsHighlightedSummarySchema'
import { NewsOrderForm } from '../../schemas/NewsOrderSchema'

async function postNewsHighlighted(data: FormData) {
  return api.backend.post('/posts', data)
}

type Form = NewsHighlightedSummaryForm &
  NewsHighlightedContentForm &
  NewsHighlightedImageForm &
  NewsOrderForm

export function useCreateNewsHighlighted() {
  const { t } = useTranslation('panel')
  const drawerCreateNewsHighlighted = useDrawer(
    'drawer-create-news-highlighted',
  )
  const { mutateAsync, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    FormData
  >({
    mutationFn: postNewsHighlighted,
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: Megaphone,
          title: t('news.use_create_news_highlighted.toast_success.title'),
        },
      })
      drawerCreateNewsHighlighted.close()
      queryClient.invalidateQueries({ queryKey: ['/posts-slider/paginate'] })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t('news.use_create_news_highlighted.toast_error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('news.use_create_news_highlighted.toast_error.description'),
        },
      })
    },
  })

  const handleCreateNewsHighlighted = async (form: Form) => {
    const data = new FormData()

    data.append('postImage', form.postImage)
    if (form.postFile) data.append('postFile', form.postFile)

    const body = {
      isSlider: true,
      title: form.title,
      subTitle: form.subtitle,
      post: {
        content: form.content,
      },
      postExternalUrl: form.postExternalUrl,
      tag: form.tag?.map((tag) => tag.value) || null,
      sliderSort: form.sliderSort,
    }

    data.append('body', JSON.stringify(body))

    await mutateAsync(data)
  }

  return {
    handleCreateNewsHighlighted,
    isCreatingNewsHighlighted: isPending,
  }
}
